import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Observable, Subject, Subscription } from "rxjs";
import { ColumnTypeEnum } from "src/app/core/enums/list-layout/column-type-enum";
import { BaseModel } from "src/app/core/models/base.model";
import { ExportColumnDef } from "src/app/core/models/reporting/export-column-def";
import { ExportType, ExportConfigModel } from "src/app/core/models/reporting/export-config-model";
import { TableConfigurationModel } from "src/app/core/models/table/table-configuration.model";
import { AuthMyfileService } from "src/app/core/services/auth.myfile.service";
import { OpenModalService } from "src/app/core/services/modal-services/open-modal.service";
import { PdfViewerModalService } from "src/app/core/services/modal-services/pdf-viewer-modal.service";
import { MyfileApiService } from "src/app/core/services/myfile-api.service";
import { ToasterNotificationService } from "src/app/core/services/toaster-notification.service";
import { GlobalComponent } from "src/app/global-component";
import { environment } from "src/environments/environment";

@Component({
  selector: 'app-base-list',
  templateUrl: './base-list.component.html',
  styleUrls: ['./base-list.component.scss']
})
export abstract class BaseListComponent implements OnInit, OnDestroy {

  //checkbox variables 
  protected masterSelected!: boolean;
  protected checkedValGet: any[] = [];
  protected unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  //Searchbar variables 
  protected searchTermLength: number = 0;
  protected minLengthTerm: number = GlobalComponent.minSearchLengthTerm;
  protected abstract searchPlaceHolderText: string;

  //Subscriber Variable references 
  protected listItems$?: Observable<any>;
  protected listItemsInput$ = new Subject<string>();
  protected listItemsObservable!: Observable<BaseModel[]>;
  protected total!: Observable<number>;
  protected isLoading!: Observable<Boolean>;

  //Current List variables 
  protected listItems: any;
  protected listItemsLoading = false;
  protected isLoadingVal: boolean = false;

  protected submitted: boolean = false;

  constructor(
    protected myFileApiService: MyfileApiService,
    protected sanitizer: DomSanitizer,
    protected pdfModalService: PdfViewerModalService,
    protected authMyFileService: AuthMyfileService,
    protected toasterNotificationService: ToasterNotificationService,
    protected openModalService: OpenModalService
  ) {
  }

  serviceSort(service: any, sortColumn: any, sortDirection: any) {
    service.sortColumnDirective = sortColumn;
    service.sortDirection = sortDirection;
    this.masterSelected = false;
  }

  exportTo(exportType: ExportType, searchModel: any, title: string, endpoint: string, tableConfig: TableConfigurationModel) {
    var exportConfig = new ExportConfigModel();
    if (tableConfig && tableConfig.columns) {
      tableConfig.columns.forEach(column => { if (column.dataFieldName) {
          if (column.type === ColumnTypeEnum.SubPhotoModel || column.type === ColumnTypeEnum.ValueInSubModel) {
            if (column.subDataFieldName !== undefined && column.subDataFieldName.length > 1) {
              exportConfig.exportColumnDefinitions.push(new ExportColumnDef(column.title, `${column.dataFieldName}.${column.subDataFieldName.charAt(0).toUpperCase() + column.subDataFieldName.slice(1)}`, column.visible, column.type, column.customDateDisplayOptionSelected))
            } else {
              exportConfig.exportColumnDefinitions.push(new ExportColumnDef(column.title, column.dataFieldName, column.visible, column.type, column.customDateDisplayOptionSelected))
            }
          } else if (column.type === ColumnTypeEnum.ValueInArrayFirstIndex) {
            if (column.subDataFieldName !== undefined && column.subDataFieldName.length > 1) {
              exportConfig.exportColumnDefinitions.push(new ExportColumnDef(column.title, `${column.dataFieldName}[0].${column.subDataFieldName.charAt(0).toUpperCase() + column.subDataFieldName.slice(1)}`, column.visible, column.type, column.customDateDisplayOptionSelected))
            } else {
              exportConfig.exportColumnDefinitions.push(new ExportColumnDef(column.title, column.dataFieldName, column.visible, column.type, column.customDateDisplayOptionSelected))
            }
          } else if (column.dataFieldName === 'hst') {
            // need to figure out away to determine if a value should be fully capitalized like here with HST on other models. For now this is a one off case so its fine
            exportConfig.exportColumnDefinitions.push(new ExportColumnDef(column.title, column.dataFieldName.toUpperCase(), column.visible, column.type, column.customDateDisplayOptionSelected))
          } else {
            exportConfig.exportColumnDefinitions.push(new ExportColumnDef(column.title, column.dataFieldName, column.visible, column.type, column.customDateDisplayOptionSelected))
          }
        }
      })
    }

    exportConfig.exportType = exportType;
    exportConfig.reportTitle = title;
    exportConfig.fileName = title;
    exportConfig.searchModel = { ...searchModel };
    exportConfig.searchModel.pageSize = GlobalComponent.defaultPagingInfo.exportPageSize;
    this.isLoadingVal = true;
    this.myFileApiService.exportList(exportConfig, endpoint).subscribe(result => {
      if (result) {
        this.toasterNotificationService.showSuccess($localize`:@@: Exported data has been downloaded to your system`, $localize`:@@Toast_Success_GeneralTitle:Success`);
      }
      this.isLoadingVal = false;
    });
  }

  checkUncheckAll(event: any, listItems: any) {
    listItems.forEach((x: { state: any; }) => x.state = event.target.checked)
    var checkedVal: any[] = [];
    var result;
    for (var i = 0; i < listItems.length; i++) {
      if (listItems[i].state == true) {
        result = listItems[i];
        checkedVal.push(result);
      }
    }
    this.checkedValGet = checkedVal
  }

  onCheckboxChange(ev: any, listItems: any) {
    var checkedVal: any[] = [];
    var result;
    for (var i = 0; i < listItems.length; i++) {
      if (listItems[i].state == true) {
        result = listItems[i];
        checkedVal.push(result);
      }
    }
    this.checkedValGet = checkedVal;
  }

  uncheckAll(list: any) {
    this.masterSelected = false;
    list.forEach((x: { state: any; }) => x.state = false)
  }

  dateRename(date: Date | undefined) {
    if (date) {
      return GlobalComponent.dateTransform(date.toString(), false)
    } else {
      return '';
    }
  }

  //alt for convert date
  // convertDate(date: any): string {
  //   var convertedDate = new Date(date);
  //   return convertedDate.toDateString();
  // }

  getDocumentImageUrl(imageUrl: string | undefined): string {
    if (imageUrl && typeof imageUrl === "string") {
      var filename = imageUrl.split('/').pop();

      if (filename && filename.includes("120.png")) {
        filename = GlobalComponent.defaultMyfileNameImage;
      }
      return environment.staticImageApiUrl + filename;
    }
    else {
      return GlobalComponent.defaultDocumentImageUrl;
    }
  }

  clearSearch(service: any) {
    service.clearListService();
  }

  pagingInfo(service: any) {
    let from = service.pageIndex == 0 ?
      (service.pageSize * service.pageIndex) + 1
      : service.pageSize * service.pageIndex;

    let to = (service.pageSize * service.pageIndex) + service.pageSize;

    if (to > service.totalRecords) {
      to = service.totalRecords;
    }

    if (from <= 0 || service.totalRecords == 0) {
      from = 0;
    }
    return `Showing ${from} to ${to} of ${service.totalRecords} records`;
  }

  selectPage(service: any, page: string) {
    service.startIndex = parseInt(page, 10) || 1;
  }

  formatPageInput(input: HTMLInputElement) {
    input.value = input.value.replace(GlobalComponent.FILTER_PAG_REGEX, '');
  }

  stopPropagationOnClick($event: MouseEvent) {
    $event.stopPropagation();
  }

  abstract ngOnInit(): void;

  abstract ngOnDestroy(): void;

}
